import React, { useMemo } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
import VideocamIcon from 'components/material-symbol/icons/Videocam'
import { useVirtualCoachingEnabled } from 'lib/pga-coach/use-virtual-coaching'
import { useVirtualCoachingSummary } from 'pages/pga-coach/virtual-coaching/api-hooks'
import {
  trackVirtualCoachingEvent,
  VIRTUAL_COACHING_EVENTS,
} from 'pages/pga-coach/virtual-coaching/analytics'
import {
  countOverdue,
  getRequestsPath,
  REQUEST_STATUS,
  REQUEST_TABS,
} from 'pages/pga-coach/virtual-coaching/utils'
import themes from 'themes'
import { PGA_COACH_ERROR_BG, PGA_COACH_ERROR_TEXT } from './dashboard-colors'

const PROMO_BG = '#E0EAFF'

const useStyles = makeStyles(theme => ({
  banner: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(2, 2.5),
    marginBottom: theme.spacing(3),
    borderRadius: 12,
    color: themes.pgaCoach.palette.primary.main,
    backgroundColor: PROMO_BG,
    [theme.breakpoints.down('xs')]: {
      alignItems: 'flex-start',
      flexDirection: 'column',
    },
  },
  overdue: {
    color: PGA_COACH_ERROR_TEXT,
    backgroundColor: PGA_COACH_ERROR_BG,
  },
  icon: {
    fontSize: 28,
    flexShrink: 0,
  },
  copy: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: 1.4,
  },
  caption: {
    fontSize: '0.875rem',
    lineHeight: 1.4,
  },
}))

const VirtualCoachingTaskBanner = () => {
  const classes = useStyles()
  const enabled = useVirtualCoachingEnabled()
  const { data } = useVirtualCoachingSummary({ skip: !enabled })

  const { readyCount, overdueCount } = useMemo(() => {
    const ready = (data?.virtualCoachingRequests || []).filter(
      request => request.status === REQUEST_STATUS.READY_TO_ANALYZE,
    )
    return { readyCount: ready.length, overdueCount: countOverdue(ready) }
  }, [data])

  if (!enabled || readyCount === 0) return null

  const isOverdue = overdueCount > 0
  const title =
    readyCount === 1
      ? '1 virtual coaching request is ready to analyze'
      : `${readyCount} virtual coaching requests are ready to analyze`
  const caption = isOverdue
    ? `${overdueCount} ${
      overdueCount === 1 ? 'is' : 'are'
    } past your turnaround time. Reply soon to keep your golfers happy.`
    : 'Golfers are waiting on your swing analysis.'

  return (
    <Box
      component="section"
      className={`${classes.banner} ${isOverdue ? classes.overdue : ''}`}
      aria-label="Virtual coaching requests"
      data-testid="virtual-coaching-task-banner"
    >
      <VideocamIcon className={classes.icon} />
      <Box className={classes.copy}>
        <Typography className={classes.title}>{title}</Typography>
        <Typography className={classes.caption}>{caption}</Typography>
      </Box>
      <Button
        variant="contained"
        color="primary"
        component={RouterLink}
        to={getRequestsPath(REQUEST_TABS.READY)}
        onClick={() =>
          trackVirtualCoachingEvent(
            VIRTUAL_COACHING_EVENTS.DASHBOARD_TASK_CLICKED,
            { readyCount, overdueCount },
          )
        }
      >
        Review requests
      </Button>
    </Box>
  )
}

export default VirtualCoachingTaskBanner
